import { CauseName } from '@beyond-js/ui';
import type { Refusal } from '@snapshots/transport';

type Text = (key: string, values?: Record<string, string | number>) => string;

/** Who did not answer a read, from the family's catalog of causes (D133): this service, an authority it needs, or its storage */
export type Cause = 'snapshots' | 'accounts' | 'projects' | 'storage';

/**
 * A read of a page that did not answer: a stable `key`, what it reads as a noun (`title`, said only in the
 * line's Technical details), the refusal that says so (its evidence) and `run`, the same read again, which
 * updates the page when it answers.
 */
export interface Missed {
	key: string;
	title: string;
	refusal: Refusal;
	run: () => Promise<unknown>;
}

/**
 * An outage of a page's reads, said once (decision D163): the reads nobody answered group by who did not
 * answer, and each group is one line in the page's notice region with when it last tried and one Try again.
 * A refusal is an answer and never an outage, and so are a session's (`Session` speaks for it), a change
 * whose outcome is unknown and a limit: each keeps its own answer.
 */
export class Outage {
	/** The causes in the order their lines are drawn */
	static causes: readonly Cause[] = ['snapshots', 'storage', 'projects', 'accounts'];
	static #dependencies: Record<string, Cause> = { accounts: 'accounts', projects: 'projects', database: 'storage', storage: 'storage' };

	/** Who did not answer a read, or null when it was answered */
	static cause(refusal: Refusal): Cause | null {
		if (refusal.quiet || refusal.uncertain || !refusal.unreachable || refusal.code === 'LIMIT_EXCEEDED') return null;
		return Outage.#dependencies[String(refusal.details?.dependency ?? '')] ?? 'snapshots';
	}

	/**
	 * Who did not answer, in the family's words: a product by its name in the catalog of causes (`CauseName`),
	 * "Beyond Snapshots did not answer", "Beyond Accounts did not answer"; the storage in Snapshots' own
	 */
	static title(text: Text, cause: Cause): string {
		return cause === 'storage' ? text('outage.storage') : text('outage.cause', { name: CauseName.of(cause) });
	}

	/** The reads nobody answered, by who did not answer; a read that was answered is left out */
	static group(reads: Missed[]): Map<Cause, Missed[]> {
		const groups = new Map<Cause, Missed[]>();
		for (const read of reads) {
			const cause = Outage.cause(read.refusal);
			if (cause) groups.set(cause, [...(groups.get(cause) ?? []), read]);
		}
		return groups;
	}
}
